'use client';

import type { FetchProductQuery } from '@eliadmin/core/src/api/products/product-api';
import type { Product } from '@eliadmin/core/src/api/slugs/types/slug-detail-types';

import { CacheKey } from '@eliadmin/core/src/api/constants';
import { fetchProducts } from '@eliadmin/core/src/api/products/product-api';
import { ProductCard } from '@eliadmin/core/src/components/product/product-card';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { Carousel } from '@eliadmin/core/src/components/ui/carousel';
import { CarouselProvider } from '@eliadmin/core/src/components/ui/carousel-context';
import { CarouselControls } from '@eliadmin/core/src/components/ui/carousel-controls';
import { CarouselProgress } from '@eliadmin/core/src/components/ui/carousel-progress';
import { Heading } from '@eliadmin/core/src/components/ui/heading';
import { Paragraph } from '@eliadmin/core/src/components/ui/paragraph';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useQuery } from '@tanstack/react-query';
import Image from 'next/image';
import Link from 'next/link';
import { GoArrowLeft, GoArrowRight } from 'react-icons/go';

interface ProductsCarouselProps {
  products: Product[];
  title: string;
  description?: string;
  topVector?: boolean;
  locale?: string;
  query?: FetchProductQuery;
  className?: {
    section?: string;
  };
}

const CAROUSEL_OPTIONS = { align: 'start' as const };

function ProductsCarouselInner({
  products,
  title,
  description,
  className,
  topVector,
}: ProductsCarouselProps) {
  return (
    <section
      className={cn(
        'custom-section',
        'relative gap-6 pr-0 sm:gap-8 md:gap-10 lg:gap-[50px] xl:pr-4',
        className?.section,
      )}
    >
      {topVector && (
        <Image
          src={'/images/bottom-white.svg'}
          alt="Products"
          width={0}
          height={0}
          className="absolute left-0 top-0 z-0 h-auto w-full"
        />
      )}
      <div
        className={cn(
          'custom-container',
          'flex flex-col items-start justify-start gap-6 pr-4 md:flex-row md:items-center md:justify-between xl:pr-0',
        )}
      >
        <Heading title={title} color="black" />
        {!!description && (
          <Paragraph className="w-full sm:max-w-[456px]" color="foreground" size="base">
            {description}
          </Paragraph>
        )}
      </div>
      <div
        className={cn('custom-container', 'overflow-visible')}
        style={{ clipPath: 'inset(-100vw -100vw -100vw 0)' }}
      >
        <Carousel
          options={CAROUSEL_OPTIONS}
          className={{
            wrapper: 'w-full overflow-visible',
            container: 'w-full gap-3 overflow-visible sm:gap-4 lg:gap-6',
            main: 'w-full overflow-visible',
            slide: 'w-full max-w-[282px] rounded-[12px] bg-white',
          }}
          slides={products.map(product => (
            <ProductCard product={product} key={product.slug} className={{ main: 'bg-white' }} />
          ))}
        />
      </div>
      <div className={cn('custom-container', 'flex items-center justify-between pr-4 xl:pr-0')}>
        <CarouselControls
          buttonProps={{ size: 'none' }}
          nextIcon={<GoArrowRight size={20} />}
          prevIcon={<GoArrowLeft size={20} />}
          className={{
            main: 'hidden sm:flex',
            button:
              'max-h-[50px] min-h-[50px] min-w-[50px] max-w-[50px] rounded-[12px] bg-[#E4F2E7] p-0 text-primary hover:bg-[#E4F2E7]',
          }}
        />
        {/* <Button variant={'primary-foreground'} className="w-full sm:w-fit">
          <Link href={'#'} prefetch={false}>
            Zobraziť všetky
          </Link>
        </Button> */}
      </div>
    </section>
  );
}

export function ProductsCarousel({
  products: initialProducts,
  locale,
  query,
  ...props
}: ProductsCarouselProps) {
  const { me, isInitialized } = useCustomerStore();
  const authToken = me?.authToken;
  const personalizedProductsQuery = useQuery({
    queryKey: [
      CacheKey.PRODUCTS,
      'homepage-products-carousel',
      locale,
      query,
      me?.customer.id ?? 'guest',
    ],
    queryFn: () =>
      fetchProducts({ locale: locale!, query, authToken }).then(response => response.data),
    enabled: isInitialized && !!authToken && !!locale && !!query,
    placeholderData: initialProducts,
    staleTime: 0,
  });
  const products = personalizedProductsQuery.data ?? initialProducts;

  return (
    <CarouselProvider>
      <ProductsCarouselInner {...props} products={products} />
    </CarouselProvider>
  );
}
